import { Card } from "@/types/v2/Card";
import { DispatchViewMode } from "@/types/v2/DispatchView";
import { firestoreToDateTime } from "@/lib/utils";
import {
  getEmployeeJobEntriesFromCard,
  JobEntry,
  JobState,
  sumHoursByState,
} from "@/lib/jobState";

/**
 * Gets the start and end of the current week (Monday - Sunday)
 * @returns Object with start (Monday 00:00) and end (Sunday 23:59:59) dates
 */
export function getCurrentWeekRange(): {
  start: Date;
  end: Date;
} {
  const now = new Date();

  // Get current day of week (0 = Sunday, 1 = Monday, etc.)
  const dayOfWeek = now.getDay();

  // Calculate days since Monday (0 = Monday, 1 = Tuesday, etc.)
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  // Calculate start of week (Monday at 00:00:00)
  const start = new Date(now);
  start.setDate(now.getDate() - daysSinceMonday);
  start.setHours(0, 0, 0, 0);

  // Calculate end of week (Sunday at 23:59:59)
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

/**
 * Checks if a card's completedDate falls within the given week range
 * @param card - The card to check
 * @param weekRange - The week range (start and end dates)
 * @returns True if card was completed in the week range
 */
export function isCardInCurrentWeek(
  card: Card,
  weekRange: { start: Date; end: Date }
): boolean {
  if (!card.completedDate) {
    return false;
  }

  const completedDate = firestoreToDateTime(card.completedDate);
  if (!completedDate) {
    return false;
  }

  return completedDate >= weekRange.start && completedDate <= weekRange.end;
}

/**
 * Sums a card's per-job hours into per-employee, per-job-state totals.
 * @param card - The card to calculate hours from
 * @returns Map of employeeId to hours bucketed by job state
 */
export function calculateEmployeeHoursByStateFromCard(
  card: Card
): Map<string, Record<JobState, number>> {
  const entriesByEmployee = new Map<string, JobEntry[]>();

  for (const entry of getEmployeeJobEntriesFromCard(card)) {
    const existing = entriesByEmployee.get(entry.employeeId) ?? [];
    existing.push(entry);
    entriesByEmployee.set(entry.employeeId, existing);
  }

  const hoursByEmployee = new Map<string, Record<JobState, number>>();
  for (const [employeeId, entries] of entriesByEmployee) {
    hoursByEmployee.set(employeeId, sumHoursByState(entries));
  }

  return hoursByEmployee;
}

/**
 * Merges board cards with completed cards based on view mode
 * @param cards - Cards currently on the board
 * @param mode - View mode (board or week)
 * @param completedCards - Additional completed cards (for week mode)
 * @returns Combined array of cards based on mode
 */
export function getCardsForDispatchView(
  cards: Card[],
  mode: DispatchViewMode,
  completedCards?: Card[]
): Card[] {
  if (mode === "board") {
    return cards;
  }

  // Week mode: merge board cards with completed cards
  if (!completedCards || completedCards.length === 0) {
    return cards;
  }

  // Create a map to deduplicate (in case a card is both on board and completed)
  const cardMap = new Map<string, Card>();

  // Add board cards first
  cards.forEach((card) => cardMap.set(card.id, card));

  // Add completed cards
  completedCards.forEach((card) => cardMap.set(card.id, card));

  return Array.from(cardMap.values());
}
